import { Fragment, useState, useEffect } from 'react';
import api from '../api/axios';

// ---------------------------------------------------------------------------
// Admin > Billing Analytics
// Shows everything saved in analytics_events: totals, funnel, per-plan and
// per-product breakdowns, and the full event list with filters.
// ---------------------------------------------------------------------------

const EVENT_LABELS = {
  pricing_page_viewed: 'Pricing page viewed',
  plan_card_viewed: 'Plan card viewed',
  plan_cta_clicked: 'Plan button clicked',
  checkout_started: 'Checkout started',
  payment_successful: 'Payment successful',
  payment_failed: 'Payment failed',
  subscription_activated: 'Subscription activated',
  subscription_upgraded: 'Subscription upgraded',
  subscription_downgraded: 'Subscription downgraded',
  candidate_credit_purchased: 'Candidate credits purchased',
  job_boost_purchased: 'Job Boost purchased',
  free_job_posted: 'Free job posted',
  free_to_paid_conversion: 'Free to paid',
  plan_cancelled: 'Plan cancelled',
};

const PRODUCT_LABELS = {
  subscription: 'Plan subscription',
  hiring_pack: 'Hiring pack',
  job_product: 'Pay-per-job',
  credit_topup: 'Candidate credits',
  job_boost: 'Job Boost',
  find_candidates: 'Find candidates',
};

const TONES = {
  good: 'bg-[#ECFDF5] text-[#10B981]',
  bad: 'bg-[#FEF2F2] text-[#EF4444]',
  warn: 'bg-[#FFFBEB] text-[#F59E0B]',
  info: 'bg-[#F5F0FA] text-[#7C3AED]',
};

const EVENT_TONE = {
  payment_successful: 'good',
  subscription_activated: 'good',
  subscription_upgraded: 'good',
  free_to_paid_conversion: 'good',
  candidate_credit_purchased: 'good',
  job_boost_purchased: 'good',
  payment_failed: 'bad',
  plan_cancelled: 'bad',
  subscription_downgraded: 'bad',
  checkout_started: 'warn',
  plan_cta_clicked: 'warn',
};

const eventLabel = (name) => EVENT_LABELS[name] || String(name || '').replace(/_/g, ' ');
const productLabel = (p) => PRODUCT_LABELS[p] || (p ? String(p).replace(/_/g, ' ') : '—');

const fmtINR = (n) =>
  '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const fmtTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return ymd(d);
};

const RANGES = [
  { label: 'Today', from: () => daysAgo(0) },
  { label: '7 days', from: () => daysAgo(6) },
  { label: '30 days', from: () => daysAgo(29) },
  { label: '90 days', from: () => daysAgo(89) },
  { label: 'All time', from: () => '2000-01-01' },
];

const INPUT =
  'border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';

const EMPTY_FILTERS = { event: '', plan: '', gateway: '', search_user: '', reference: '' };

export default function BillingAnalyticsPanel() {
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(ymd(new Date()));

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState('');

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [events, setEvents] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

    const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Typing in the text filters waits a moment before searching.
  useEffect(() => {
    const t = setTimeout(() => {
      setApplied(filters);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setSummaryLoading(true);
      setSummaryError('');
      try {
        const { data } = await api.get('/admin/billing-analytics/summary', { params: { from, to } });
        if (!cancelled) setSummary(data);
      } catch (err) {
        if (!cancelled) setSummaryError(err.response?.data?.message || 'Could not load the summary.');
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [from, to, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setEventsLoading(true);
      setEventsError('');
      try {
        const { data } = await api.get('/admin/billing-analytics/events', {
          params: {
            from,
            to,
            page,
            per_page: perPage,
            event: applied.event || undefined,
            plan: applied.plan || undefined,
            gateway: applied.gateway || undefined,
            search_user: applied.search_user || undefined,
            reference: applied.reference || undefined,
          },
        });
        if (!cancelled) setEvents(data);
      } catch (err) {
        if (!cancelled) setEventsError(err.response?.data?.message || 'Could not load the events.');
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [from, to, applied, page, perPage, reloadKey]);

  useEffect(() => {
    setSelectedIds([]);
  }, [events]);

  const ev = (name) => summary?.events?.[name] || { total: 0, users: 0, amount: 0 };

  const paid = ev('payment_successful');
  const failed = ev('payment_failed');
  const attempts = paid.total + failed.total;
  const failRate = attempts > 0 ? Math.round((failed.total / attempts) * 100) : 0;

  const cards = [
    { label: 'Pricing page views', value: ev('pricing_page_viewed').total, sub: `${ev('pricing_page_viewed').users} people`, text: 'text-[#7C3AED]', bar: 'bg-[#7C3AED]' },
    { label: 'Plan cards viewed', value: ev('plan_card_viewed').total, sub: `${ev('plan_card_viewed').users} people`, text: 'text-[#7C3AED]', bar: 'bg-[#7C3AED]' },
    { label: 'Plan buttons clicked', value: ev('plan_cta_clicked').total, sub: `${ev('plan_cta_clicked').users} people`, text: 'text-[#F59E0B]', bar: 'bg-[#F59E0B]' },
    { label: 'Checkouts started', value: ev('checkout_started').total, sub: `${ev('checkout_started').users} people`, text: 'text-[#F59E0B]', bar: 'bg-[#F59E0B]' },
    { label: 'Successful payments', value: paid.total, sub: `${paid.users} people`, text: 'text-[#10B981]', bar: 'bg-[#10B981]' },
    { label: 'Failed payments', value: failed.total, sub: attempts > 0 ? `${failRate}% of payment attempts` : 'No attempts yet', text: 'text-red-500', bar: 'bg-red-500' },
    { label: 'Revenue', value: fmtINR(paid.amount), sub: 'From successful payments', text: 'text-[#10B981]', bar: 'bg-[#10B981]' },
    { label: 'Subscriptions activated', value: ev('subscription_activated').total, sub: `${ev('subscription_activated').users} people`, text: 'text-[#7C3AED]', bar: 'bg-[#7C3AED]' },
    { label: 'Upgrades', value: ev('subscription_upgraded').total, sub: `${ev('subscription_upgraded').users} people`, text: 'text-[#7C3AED]', bar: 'bg-[#7C3AED]' },
    { label: 'Free to paid', value: ev('free_to_paid_conversion').total, sub: 'First-ever payment', text: 'text-[#10B981]', bar: 'bg-[#10B981]' },
    { label: 'Cancellations', value: ev('plan_cancelled').total, sub: 'Auto-renew switched off', text: 'text-red-500', bar: 'bg-red-500' },
    { label: 'Credit packs bought', value: ev('candidate_credit_purchased').total, sub: `${ev('candidate_credit_purchased').users} people`, text: 'text-[#DB2777]', bar: 'bg-[#DB2777]' },
    { label: 'Job Boosts bought', value: ev('job_boost_purchased').total, sub: `${ev('job_boost_purchased').users} people`, text: 'text-[#DB2777]', bar: 'bg-[#DB2777]' },
    { label: 'Free jobs posted', value: ev('free_job_posted').total, sub: `${ev('free_job_posted').users} companies`, text: 'text-[#0D1B3D]', bar: 'bg-[#0D1B3D]' },
  ];

  const funnel = [
    { key: 'pricing_page_viewed', label: 'Viewed pricing' },
    { key: 'plan_cta_clicked', label: 'Clicked a plan button' },
    { key: 'checkout_started', label: 'Started checkout' },
    { key: 'payment_successful', label: 'Paid' },
  ].map((s) => ({ ...s, people: ev(s.key).users }));
  const funnelTop = Math.max(funnel[0].people, 1);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const hasFilters = Object.values(filters).some(Boolean);

  const rows = events?.data || [];
  const total = events?.total || 0;
  const lastPage = events?.last_page || 1;
  const curPage = events?.current_page || 1;
  const fromRow = total === 0 ? 0 : (curPage - 1) * perPage + 1;
  const toRow = Math.min(curPage * perPage, total);
  const pageNums = [];
  for (let p = Math.max(1, curPage - 2); p <= Math.min(lastPage, curPage + 2); p++) pageNums.push(p);
  const pageBtn = 'px-3 py-1.5 rounded-lg text-xs font-semibold border disabled:opacity-40';

  const options = summary?.options || { events: [], plans: [], gateways: [] };


    const allSelected = rows.length > 0 && rows.every((r) => selectedIds.includes(r.id));
  const toggleOne = (id) =>
    setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelectedIds(allSelected ? [] : rows.map((r) => r.id));

  const afterDelete = (count) => {
    if (count >= rows.length && curPage > 1) setPage(curPage - 1);
    setReloadKey((k) => k + 1);
  };

  const deleteOne = async (id) => {
    if (!window.confirm('Delete this event permanently?')) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/billing-analytics/events/${id}`);
      afterDelete(1);
    } catch (err) {
      alert(err.response?.data?.message || 'Could not delete the event.');
    } finally {
      setDeleting(false);
    }
  };

  const deleteSelected = async () => {
    const n = selectedIds.length;
    if (n === 0) return;
    if (!window.confirm(`Delete ${n} selected ${n === 1 ? 'event' : 'events'} permanently?`)) return;
    setDeleting(true);
    try {
      await api.post('/admin/billing-analytics/events/bulk-delete', { ids: selectedIds });
      afterDelete(n);
    } catch (err) {
      alert(err.response?.data?.message || 'Could not delete the selected events.');
    } finally {
      setDeleting(false);
    }
  };
  return (
    <div className="space-y-4">
      {/* Date range */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-2 flex-wrap">
          {RANGES.map((r) => {
            const active = from === r.from() && to === ymd(new Date());
            return (
              <button
                key={r.label}
                type="button"
                onClick={() => {
                  setFrom(r.from());
                  setTo(ymd(new Date()));
                  setPage(1);
                }}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
                  active
                    ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {r.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 sm:ml-auto">
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
            className={INPUT}
          />
          <span className="text-gray-400 text-sm">to</span>
          <input
            type="date"
            value={to}
            min={from}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
            className={INPUT}
          />
        </div>
      </div>

      {summaryError && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center text-red-600 text-sm">
          {summaryError}
        </div>
      )}

      {summaryLoading && !summary && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center text-gray-400">
          Loading analytics...
        </div>
      )}

      {summary && (
        <>
          {/* Summary cards */}
          <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 transition-opacity ${summaryLoading ? 'opacity-60' : ''}`}>
            {cards.map((c) => (
              <div
                key={c.label}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 relative overflow-hidden"
              >
                <span className={`absolute top-0 left-0 w-full h-1 ${c.bar}`}></span>
                <p className={`text-3xl font-extrabold tracking-tight ${c.text}`}>{c.value}</p>
                <p className="text-sm font-semibold text-[#0D1B3D] mt-1">{c.label}</p>
                <p className="text-xs text-gray-400 mt-0.5">{c.sub}</p>
              </div>
            ))}
          </div>

          {/* Funnel */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-bold text-[#0D1B3D] text-lg">Buying funnel</h3>
            <p className="text-sm text-gray-500 mb-4">Counts each person once per step, so repeat visits do not inflate it.</p>
            <div className="space-y-4">
              {funnel.map((s, i) => {
                const prev = i > 0 ? funnel[i - 1].people : 0;
                const kept = prev > 0 ? Math.min(100, Math.round((s.people / prev) * 100)) : null;
                return (
                  <div key={s.key}>
                    <div className="flex items-baseline justify-between gap-3 flex-wrap text-sm mb-1">
                      <span className="font-medium text-[#0D1B3D]">{s.label}</span>
                      <span className="text-gray-500">
                        {s.people} {s.people === 1 ? 'person' : 'people'}
                        {kept !== null && `, ${kept}% of the previous step`}
                      </span>
                    </div>
                    <div className="h-3 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899]"
                        style={{ width: `${Math.min(100, (s.people / funnelTop) * 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Per plan + per product */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-bold text-[#0D1B3D]">By plan or item</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-gray-50/60 text-gray-400 text-[10px] uppercase border-b border-gray-100">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Plan or item</th>
                      <th className="text-left px-4 py-3 font-semibold">Card views</th>
                      <th className="text-left px-4 py-3 font-semibold">Clicks</th>
                      <th className="text-left px-4 py-3 font-semibold">Checkouts</th>
                      <th className="text-left px-4 py-3 font-semibold">Paid</th>
                      <th className="text-left px-4 py-3 font-semibold">Failed</th>
                      <th className="text-left px-4 py-3 font-semibold">Paid / checkout</th>
                      <th className="text-left px-4 py-3 font-semibold">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.plans.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                          No plan activity in this date range.
                        </td>
                      </tr>
                    ) : (
                      summary.plans.map((p) => (
                        <tr key={p.plan_name} className="border-t border-gray-50 hover:bg-purple-50/30 transition-colors">
                          <td className="px-4 py-3 font-medium">{p.plan_name}</td>
                          <td className="px-4 py-3">{p.card_views}</td>
                          <td className="px-4 py-3">{p.clicks}</td>
                          <td className="px-4 py-3">{p.checkouts}</td>
                          <td className="px-4 py-3">{p.payments}</td>
                          <td className="px-4 py-3">{p.failed}</td>
                          <td className="px-4 py-3">
                            {p.checkouts > 0 ? `${Math.round((p.payments / p.checkouts) * 100)}%` : '—'}
                          </td>
                          <td className="px-4 py-3 font-semibold">{fmtINR(p.revenue)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-bold text-[#0D1B3D]">Revenue by product</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50/60 text-gray-400 text-[10px] uppercase border-b border-gray-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Product</th>
                    <th className="text-left px-4 py-3 font-semibold">Payments</th>
                    <th className="text-left px-4 py-3 font-semibold">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.products.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-6 text-center text-gray-400">
                        No payments in this date range.
                      </td>
                    </tr>
                  ) : (
                    summary.products.map((p) => (
                      <tr key={p.product} className="border-t border-gray-50 hover:bg-purple-50/30 transition-colors">
                        <td className="px-4 py-3 font-medium">{productLabel(p.product)}</td>
                        <td className="px-4 py-3">{p.payments}</td>
                        <td className="px-4 py-3 font-semibold">{fmtINR(p.revenue)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Event list */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
        <h3 className="font-bold text-[#0D1B3D]">All events</h3>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <select value={filters.event} onChange={(e) => setFilter('event', e.target.value)} className={INPUT}>
            <option value="">Event: All</option>
            {options.events.map((name) => (
              <option key={name} value={name}>{eventLabel(name)}</option>
            ))}
          </select>
          <select value={filters.plan} onChange={(e) => setFilter('plan', e.target.value)} className={INPUT}>
            <option value="">Plan or item: All</option>
            {options.plans.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <select value={filters.gateway} onChange={(e) => setFilter('gateway', e.target.value)} className={INPUT}>
            <option value="">Gateway: All</option>
            {options.gateways.map((name) => (
              <option key={name} value={name} className="capitalize">{name}</option>
            ))}
          </select>
          <input
            type="text"
            value={filters.search_user}
            onChange={(e) => setFilter('search_user', e.target.value)}
            placeholder="User name, email, phone or ID"
            className={INPUT}
          />
          <input
            type="text"
            value={filters.reference}
            onChange={(e) => setFilter('reference', e.target.value)}
            placeholder="Order or subscription reference"
            className={INPUT}
          />
        </div>
        {hasFilters && (
          <button
            type="button"
            onClick={() => setFilters(EMPTY_FILTERS)}
            className="text-xs font-semibold text-[#7C3AED] hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

            {selectedIds.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-3 bg-red-50 border border-red-100 rounded-2xl px-4 py-3">
          <span className="text-sm font-medium text-red-600">
            {selectedIds.length} {selectedIds.length === 1 ? 'event' : 'events'} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              className="text-xs font-semibold text-gray-600 px-3 py-1.5 rounded-lg bg-white border border-gray-200"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={deleteSelected}
              disabled={deleting}
              className="text-xs font-semibold text-white px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 disabled:opacity-50"
            >
              {deleting ? 'Deleting…' : 'Delete selected'}
            </button>
          </div>
        </div>
      )}

      <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto transition-opacity ${eventsLoading ? 'opacity-60' : ''}`}>
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-gray-50/60 text-gray-400 text-[10px] uppercase border-b border-gray-100">
            <tr>
              <th className="px-4 py-3 w-8">
  <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={rows.length === 0} />
</th>
              <th className="text-left px-4 py-3 font-semibold">Time</th>
              <th className="text-left px-4 py-3 font-semibold">Event</th>
              <th className="text-left px-4 py-3 font-semibold">User</th>
              <th className="text-left px-4 py-3 font-semibold">Plan or item</th>
              <th className="text-left px-4 py-3 font-semibold">Product</th>
              <th className="text-left px-4 py-3 font-semibold">Amount</th>
              <th className="text-left px-4 py-3 font-semibold">Gateway</th>
              <th className="text-left px-4 py-3 font-semibold">Reference</th>
              <th className="text-left px-4 py-3 font-semibold">Details</th>
           <th className="text-left px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {eventsError ? (
              <tr><td colSpan={11} className="px-4 py-6 text-center text-red-600">{eventsError}</td></tr>
            ) : !events ? (
              <tr><td colSpan={11} className="px-4 py-6 text-center text-gray-400">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-4 py-6 text-center text-gray-400">
                  {hasFilters ? 'No events match these filters.' : 'No events in this date range.'}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const props = row.properties && typeof row.properties === 'object' ? Object.entries(row.properties) : [];
                const open = expandedId === row.id;
                return (
                  <Fragment key={row.id}>
                    <tr className="border-t border-gray-50 hover:bg-purple-50/30 transition-colors">
                    <td className="px-4 py-3">
  <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => toggleOne(row.id)} />
</td>
                    
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtTime(row.created_at)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${TONES[EVENT_TONE[row.event_name] || 'info']}`}>
                          {eventLabel(row.event_name)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {row.user_name || row.user_email ? (
                          <>
                            <p className="font-medium">{row.user_name || '—'}</p>
                            <p className="text-xs text-gray-500 max-w-[200px] truncate" title={row.user_email}>{row.user_email}</p>
                          </>
                        ) : (
                          <span className="text-gray-400">{row.user_id ? `User #${row.user_id}` : '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">{row.plan_name || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {row.source_type ? productLabel(row.source_type) : row.plan_name ? productLabel('subscription') : '—'}
                      </td>
                      <td className="px-4 py-3 font-semibold">{row.amount != null ? fmtINR(row.amount) : '—'}</td>
                      <td className="px-4 py-3 capitalize">{row.gateway || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 max-w-[180px] truncate" title={row.reference || ''}>
                        {row.reference || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setExpandedId(open ? null : row.id)}
                          className="text-xs font-semibold text-[#7C3AED] hover:underline"
                        >
                          {open ? 'Hide' : 'Show'}
                        </button>
                      </td>

                      <td className="px-4 py-3">
  <button
    type="button"
    onClick={() => deleteOne(row.id)}
    disabled={deleting}
    className="text-xs font-semibold text-red-500 hover:underline disabled:opacity-50"
  >
    Delete
  </button>
</td>
                    </tr>
                    {open && (
                      <tr key={`${row.id}-details`} className="bg-[#F5F0FA]/40 border-t border-gray-50">
                        <td colSpan={11} className="px-4 py-3">
                          <dl className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-xs">
                            <div>
                              <dt className="text-gray-400">Event ID</dt>
                              <dd className="font-medium text-gray-700">#{row.id}</dd>
                            </div>
                            <div>
                              <dt className="text-gray-400">User ID</dt>
                              <dd className="font-medium text-gray-700">{row.user_id ?? '—'}</dd>
                            </div>
                            <div>
                              <dt className="text-gray-400">User phone</dt>
                              <dd className="font-medium text-gray-700">{row.user_phone || '—'}</dd>
                            </div>
                            <div>
                              <dt className="text-gray-400">Duplicate-check key</dt>
                              <dd className="font-medium text-gray-700 break-all">{row.dedupe_key || '—'}</dd>
                            </div>
                            {props.map(([k, v]) => (
                              <div key={k}>
                                <dt className="text-gray-400">{k.replace(/_/g, ' ')}</dt>
                                <dd className="font-medium text-gray-700 break-all">
                                  {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between flex-wrap gap-3 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <span>Showing {fromRow}-{toRow} of {total}</span>
          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
            className="border rounded-lg px-2 py-1 text-xs"
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>{n} / page</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1">
          <button className={pageBtn} disabled={curPage === 1} onClick={() => setPage(1)}>«</button>
          <button className={pageBtn} disabled={curPage === 1} onClick={() => setPage(curPage - 1)}>Prev</button>
          {pageNums.map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`${pageBtn} ${p === curPage ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-[#7C3AED]' : 'bg-white'}`}
            >
              {p}
            </button>
          ))}
          <button className={pageBtn} disabled={curPage === lastPage} onClick={() => setPage(curPage + 1)}>Next</button>
          <button className={pageBtn} disabled={curPage === lastPage} onClick={() => setPage(lastPage)}>»</button>
        </div>
      </div>
    </div>
  );
}